import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { EnhancedCreditCard } from '../components/ui/EnhancedCreditCard';
import { Button } from '../components/ui/Button';
import { mockCreditCards } from '../data/mockData';
import { cardAPI } from '../services/cardAPI';
import { formatCurrency } from '../utils/formatters';
import { CreditCard, Plus, Eye, EyeOff, Lock, ShoppingCart, Percent, TrendingUp, DollarSign } from 'lucide-react';
import {
  StyledCardsContainer,
  StyledHeader,
  StyledTitle,
  StyledSubtitle,
  StyledStatsGrid,
  StyledStatCard,
  StyledStatLabel,
  StyledStatValue,
  StyledCardsSection,
  StyledSectionHeader,
  StyledSectionTitle,
  StyledToggleContainer,
  StyledToggleButton,
  StyledToggleLabel,
  StyledQuickActions,
  StyledActionCard,
  StyledActionHeader,
  StyledActionIcon,
  StyledActionTitle,
  StyledActionDescription,
  StyledEmptyState,
} from './CardsPage.styles';

const CardsPage: React.FC = () => {
  const { t } = useTranslation();
  const [showCardNumbers, setShowCardNumbers] = useState(false);
  const [cards, setCards] = useState(mockCreditCards);
  const [loading, setLoading] = useState(false);

  // Load cards from API
  useEffect(() => {
    loadCards();
  }, []);

  const loadCards = async () => {
    try {
      setLoading(true);
      // For now, fall back to mock data if API fails
      // In production, you would handle the API call properly
      const apiCards = await cardAPI.getCards();

      if (apiCards && apiCards.length > 0) {
        setCards(apiCards);
      }
    } catch (err) {
      console.warn('Failed to load cards from API, using mock data:', err);
      // Continue with mock data for development
    } finally {
      setLoading(false);
    }
  };

  // Handler functions for card operations
  const handleAddCard = async () => {
    try {
      setLoading(true);
      // For demo purposes, show a modal/form placeholder
      const confirmed = window.confirm(
        'Add Card Feature\\n\\nThis would open a form to add a new credit card.\\n\\nWould you like to simulate adding a card?',
      );

      if (confirmed) {
        // Simulate adding a card with the API
        const newCardData = {
          name: 'New Demo Card',
          type: 'gold' as const,
          number: '4444 5555 6666 7777',
          limit: 3000,
          expiry: '12/28',
          apr: 19.99,
        };

        const newCard = await cardAPI.addCard(newCardData);

        setCards((prev) => [...prev, newCard]);
        alert('Card added successfully!');
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';

      alert(`Failed to add card: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFreezeCard = async () => {
    if (cards.length === 0) {
      alert('No cards available to freeze');

      return;
    }

    try {
      setLoading(true);
      const cardToFreeze = cards[0]; // For demo, freeze the first card
      const confirmed = window.confirm(
        `Freeze Card: ${cardToFreeze.name}\\n\\nThis will temporarily block all transactions on this card.\\n\\nContinue?`,
      );

      if (confirmed) {
        await cardAPI.freezeCard(cardToFreeze.id);
        await loadCards(); // Reload cards to get updated status
        alert('Card frozen successfully!');
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';

      alert(`Failed to freeze card: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleViewRewards = async () => {
    if (cards.length === 0) {
      alert('No cards available');

      return;
    }

    try {
      setLoading(true);
      const cardId = cards[0].id; // For demo, use the first card
      const rewards = await cardAPI.getCardRewards(cardId);

      alert(
        'Rewards Summary\\n\\n' +
          `Total Points: ${rewards.total_points.toLocaleString()}\\n` +
          `Available Points: ${rewards.available_points.toLocaleString()}\\n` +
          `Tier: ${rewards.tier}\\n` +
          `Cashback Rate: ${rewards.cashback_rate}%\\n\\n` +
          `Top Redemption Options:\\n${rewards.redemption_options
            .slice(0, 3)
            .map((option) => `• ${option.name}: ${option.points_required} pts → $${option.value}`)
            .join('\\n')}`,
      );
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';

      alert(`Failed to load rewards: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSpendingInsights = async () => {
    if (cards.length === 0) {
      alert('No cards available');

      return;
    }

    try {
      setLoading(true);
      const cardId = cards[0].id; // For demo, use the first card
      const insights = await cardAPI.getSpendingInsights(cardId);

      const topCategories = insights.category_breakdown
        .slice(0, 3)
        .map((cat) => `${cat.category}: $${cat.amount.toFixed(2)} (${cat.percentage}%)`)
        .join('\\n');

      alert(
        'Spending Insights\\n\\n' +
          `Total Spent (${insights.period}): $${insights.total_spent.toFixed(2)}\\n\\n` +
          `Top Categories:\\n${topCategories}\\n\\n` +
          'This would typically open a detailed analytics dashboard.',
      );
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';

      alert(`Failed to load spending insights: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const stats = React.useMemo(() => {
    const totalBalance = cards.reduce((sum, card) => sum + card.balance, 0);
    const totalLimit = cards.reduce((sum, card) => sum + card.limit, 0);
    const totalSpent = cards.reduce((sum, card) => sum + (card.limit - card.balance), 0);
    const utilization = totalLimit > 0 ? (totalSpent / totalLimit) * 100 : 0;

    return {
      totalCards: cards.length,
      totalBalance,
      totalLimit,
      totalSpent,
      utilization: Math.round(utilization),
    };
  }, [cards]);

  const quickActions = [
    {
      id: 'add-card',
      title: t('cards.actions.addCard'),
      description: t('cards.actions.addCardDesc'),
      icon: Plus,
      color: '#6366f1',
      onClick: handleAddCard,
    },
    {
      id: 'freeze-card',
      title: t('cards.actions.freezeCard'),
      description: t('cards.actions.freezeCardDesc'),
      icon: Lock,
      color: '#f59e0b',
      onClick: handleFreezeCard,
    },
    {
      id: 'view-rewards',
      title: t('cards.actions.viewRewards'),
      description: t('cards.actions.viewRewardsDesc'),
      icon: Percent,
      color: '#10b981',
      onClick: handleViewRewards,
    },
    {
      id: 'spending-insights',
      title: t('cards.actions.spendingInsights'),
      description: t('cards.actions.spendingInsightsDesc'),
      icon: TrendingUp,
      color: '#8b5cf6',
      onClick: handleSpendingInsights,
    },
  ];

  return (
    <StyledCardsContainer>
      <StyledHeader>
        <StyledTitle>
          <CreditCard size={32} />
          {t('cards.title')}
        </StyledTitle>
        <StyledSubtitle>{t('cards.subtitle')}</StyledSubtitle>
      </StyledHeader>

      <StyledStatsGrid>
        <StyledStatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <StyledStatLabel>
            <CreditCard size={16} />
            {t('cards.stats.totalCards')}
          </StyledStatLabel>
          <StyledStatValue>{stats.totalCards}</StyledStatValue>
        </StyledStatCard>

        <StyledStatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <StyledStatLabel>
            <DollarSign size={16} />
            {t('cards.stats.totalBalance')}
          </StyledStatLabel>
          <StyledStatValue>{formatCurrency(stats.totalBalance)}</StyledStatValue>
        </StyledStatCard>

        <StyledStatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <StyledStatLabel>
            <ShoppingCart size={16} />
            {t('cards.stats.totalSpent')}
          </StyledStatLabel>
          <StyledStatValue>{formatCurrency(stats.totalSpent)}</StyledStatValue>
        </StyledStatCard>

        <StyledStatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <StyledStatLabel>
            <Percent size={16} />
            {t('cards.stats.utilization')}
          </StyledStatLabel>
          <StyledStatValue>{stats.utilization}%</StyledStatValue>
        </StyledStatCard>
      </StyledStatsGrid>

      <StyledCardsSection>
        <StyledSectionHeader>
          <StyledSectionTitle>{t('cards.myCards')}</StyledSectionTitle>
          <StyledToggleContainer>
            <StyledToggleLabel>{t('cards.showNumbers')}</StyledToggleLabel>
            <StyledToggleButton
              $active={showCardNumbers}
              onClick={() => setShowCardNumbers(!showCardNumbers)}
              variant='ghost'
              disabled={loading}
            >
              {showCardNumbers ? <EyeOff size={16} /> : <Eye size={16} />}
            </StyledToggleButton>
          </StyledToggleContainer>
        </StyledSectionHeader>

        {cards.length === 0 ? (
          <StyledEmptyState>
            <div
              style={{
                width: '4rem',
                height: '4rem',
                borderRadius: '50%',
                background: 'var(--background-tertiary)',
                color: 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem',
              }}
            >
              <CreditCard size={24} />
            </div>
            <h3
              style={{
                color: 'var(--text-primary)',
                fontSize: '1.25rem',
                fontWeight: '600',
                margin: '0 0 0.5rem 0',
              }}
            >
              {t('cards.empty.title')}
            </h3>
            <p
              style={{
                color: 'var(--text-secondary)',
                margin: '0 0 2rem 0',
                maxWidth: '400px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              {t('cards.empty.description')}
            </p>
            <Button variant='primary' onClick={handleAddCard}>
              <Plus size={16} />
              {loading ? 'Loading...' : t('cards.addFirstCard')}
            </Button>
          </StyledEmptyState>
        ) : (
          <EnhancedCreditCard cards={cards} showNumbers={showCardNumbers} />
        )}
      </StyledCardsSection>

      <StyledQuickActions>
        {quickActions.map((action, index) => (
          <StyledActionCard
            key={action.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: loading ? 0.6 : 1, y: 0 }}
            transition={{ delay: 0.1 * (index + 1) }}
            onClick={loading ? undefined : action.onClick}
            style={
              {
                cursor: loading ? 'not-allowed' : 'pointer',
                pointerEvents: loading ? 'none' : 'auto',
              } as React.CSSProperties
            }
          >
            <StyledActionHeader>
              <StyledActionIcon $color={action.color}>
                <action.icon size={24} />
              </StyledActionIcon>
              <StyledActionTitle>{action.title}</StyledActionTitle>
            </StyledActionHeader>
            <StyledActionDescription>{action.description}</StyledActionDescription>
          </StyledActionCard>
        ))}
      </StyledQuickActions>
    </StyledCardsContainer>
  );
};

export default CardsPage;
